import { ZuiShellBase } from './zui-shell-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-shell-context-switcher-css.js';

/**
 * A child element of `<zui-shell-nav>`, when used it is the first item to be seen in the nav, before `<zui-shell-nav-item>`'s. It provides contextual details for what is seen in `<zui-shell-content>`, i.e. viewing as a specific company.
 *
 * @element zui-shell-context-switcher
 *
 */
export class ZuiShellContextSwitcher extends ZuiShellBase {
  /**
   * The title text represeting who / what the context is
   */
  @property({ type: String, attribute: 'context-title' })
  contextTitle: string | null = null;

  /**
   * Label above the title text, i.e. 'Viewing as'
   */
  @property({ type: String, attribute: 'context-label' })
  contextLabel: string | null = null;

  /**
   * The text beneath the switch profile icon, i.e. 'Switch'
   */
  @property({ type: String, attribute: 'action-text' })
  actionText: string | null = null;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this._observeNavCollapseChange = true;
  }

  firstUpdated() {
    this.#overrideParentAnchorStyles();
  }

  render() {
    return html`<div
      class="container ${classMap({ 'nav-collapsed': this._isNavCollapsed })}"
      title="${this.contextTitle}"
    >
      <div class="context-switcher-info">
        <span class="context-label">${this.contextLabel}</span>
        <span class="context-title">${this.contextTitle}</span>
      </div>
      <div class="context-switcher-icon">
        <zui-icon icon="zui-switch-profile" class="small"></zui-icon>
        <span class="context-switcher-icon-text">${this.actionText}</span>
      </div>
      <div class="context-switcher-info-fade" title="${this.contextTitle}">
        <span class="context-label">${this.contextLabel}</span>
        <span class="context-title">${this.contextTitle}</span>
      </div>
    </div>`;
  }

  /* If context switcher is wrapped in an anchor tag,
   * remove the anchor styles so that the context switcher
   * styles don't get overridden by the anchor styles
   */
  #overrideParentAnchorStyles() {
    const parent: HTMLElement | HTMLAnchorElement | null = this.parentElement;
    if (parent instanceof HTMLAnchorElement) {
      parent.style.textDecoration = 'none';
      parent.style.color = 'inherit';
    }
  }
}

window.customElements.define('zui-shell-context-switcher', ZuiShellContextSwitcher);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-context-switcher': ZuiShellContextSwitcher;
  }
}
